import type { NormalisedRequest } from "@onhandtech/onhand-fe-shared/lib/api/v4/requests/requests.types";
import type { NormalisedTaskType } from "@onhandtech/onhand-fe-shared/lib/api/v4/task-types/task-types.types";
import type { NormalisedRequestFilterSet } from "@onhandtech/onhand-fe-shared/lib/api/v5/request-filter-set/request-filter-set.types";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { ContentPublishStatus } from "constants/publish-status";

import { decompileRequestFilters } from "./edit-sections.utils";
import type {
	SectionFilterType,
	SectionModalType,
	SectionState,
} from "./sections.types";
import { moduleName } from "./sections.types";

const initialState: SectionState = {
	selectedSection: null,
	draftFilters: [],
	activeFilterType: ContentPublishStatus.PUBLIC,
	modals: {
		"change-section-order": null,
		"delete-section": null,
		"edit-section-name": null,
		permissions: null,
		unpublish: null,
	},
	automaticallyRemovingPublicPermission: false,
	pagination: {
		pageNumber: 1,
	},
};

const slice = createSlice({
	name: moduleName,
	initialState,
	reducers: {
		setSelectedSection: (
			state,
			action: PayloadAction<NormalisedRequestFilterSet | null>,
		) => {
			state.selectedSection = action.payload;
			state.draftFilters = decompileRequestFilters(
				action.payload?.filters ?? [],
			);
		},
		addFilter: (state, action: PayloadAction<SectionFilterType>) => {
			state.draftFilters.push(action.payload);
		},
		removeFilter: (
			state,
			action: PayloadAction<NormalisedRequest | NormalisedTaskType>,
		) => {
			state.draftFilters = state.draftFilters.filter(
				({ filter }) =>
					JSON.stringify(filter) !== JSON.stringify(action.payload),
			);
		},
		clearFilters: (state) => {
			state.draftFilters = [];
		},
		reorderFilters: (state, action: PayloadAction<SectionFilterType[]>) => {
			state.draftFilters = action.payload;
		},
		setActiveFilterType: (
			state,
			action: PayloadAction<ContentPublishStatus>,
		) => {
			state.activeFilterType = action.payload;
			state.pagination.pageNumber = 1;
		},
		setSectionModal: (
			state,
			{
				payload: { type, requestFilterSet },
			}: PayloadAction<{
				type: SectionModalType;
				requestFilterSet: NormalisedRequestFilterSet | null;
			}>,
		) => {
			state.modals[type] = requestFilterSet;
		},
		setAutomaticallyRemovingPublicPermission: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.automaticallyRemovingPublicPermission = action.payload;
		},
		setPageNumber: (state, action: PayloadAction<number>) => {
			state.pagination.pageNumber = action.payload;
		},
	},
});

export default slice;
